/**
 * Cztery lżejsze moduły HR w jednym pliku (spec 01 §10–§12a):
 * workflow, rozwój, raporty i konfiguracja.
 *
 * Każdy z nich to jedna lista albo siatka kafli na realnych danych — trzymanie
 * ich razem oszczędza czterech niemal identycznych szkieletów, a każdy ekran
 * i tak ma własną trasę.
 */
import { useId, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  Badge,
  Button,
  Card,
  ChartBar,
  ChevronRight,
  EmptyState,
  FormField,
  GraduationCap,
  InfoBanner,
  Input,
  Modal,
  Select,
  Settings,
  Spinner,
  StatCard,
  StatusPill,
  Workflow as WorkflowIcon,
  cx,
  useToast,
} from '@k1p4/ui'
import { formatPLN } from '@k1p4/i18n'
import { DEV_ITEM_TRANSITIONS, type DevItemStatus } from '@k1p4/shared'
import { ApiError, api, openFile, apiYear } from '../../api/client'
import { useHasPermission } from '../../auth/AuthProvider'
import { useFormat } from '../../format'
import styles from './HrModules.module.css'

// ---------------------------------------------------------------------------
// Workflow (spec 01 §10)
// ---------------------------------------------------------------------------

export function Workflows() {
  const { t } = useTranslation()
  const toast = useToast()
  const queryClient = useQueryClient()
  const ids = { name: useId(), description: useId(), chain: useId() }

  const [formOpen, setFormOpen] = useState(false)
  const [form, setForm] = useState({ name: '', description: '', chain: '' })
  const [error, setError] = useState<string | null>(null)

  const workflows = useQuery({ queryKey: ['workflows'], queryFn: () => api.workflows() })

  const create = useMutation({
    mutationFn: () =>
      api.createWorkflow({
        name: form.name.trim(),
        ...(form.description.trim() ? { description: form.description.trim() } : {}),
        chain: form.chain
          .split(',')
          .map((step) => step.trim())
          .filter((step) => step.length > 0),
        status: 'active',
      }),
    onSuccess: () => {
      toast.show(t('hrWorkflows.created'), { tone: 'success' })
      setFormOpen(false)
      setForm({ name: '', description: '', chain: '' })
      void queryClient.invalidateQueries({ queryKey: ['workflows'] })
    },
    onError: () => setError('hrWorkflows.errorGeneric'),
  })

  const toggle = useMutation({
    mutationFn: (id: string) => api.toggleWorkflow(id),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['workflows'] }),
    onError: () => setError('hrWorkflows.errorGeneric'),
  })

  if (workflows.isPending) return <Spinner label={t('common.loading')} />

  const items = workflows.data?.items ?? []
  const chainSteps = form.chain.split(',').filter((step) => step.trim().length > 0)

  return (
    <>
      <div className={styles.header}>
        <p className={styles.lead}>{t('hrWorkflows.lead')}</p>
        <Button
          onClick={() => {
            setError(null)
            setFormOpen(true)
          }}
        >
          {t('hrWorkflows.add')}
        </Button>
      </div>

      {error !== null ? (
        <InfoBanner tone="danger" className={styles.banner}>
          {t(error)}
        </InfoBanner>
      ) : null}

      {items.length === 0 ? (
        <EmptyState
          icon={<WorkflowIcon size={28} />}
          title={t('hrWorkflows.empty')}
          description={t('hrWorkflows.emptyBody')}
        />
      ) : (
        <div className={styles.cards}>
          {items.map((item) => (
            <Card key={item.id} pad="md" className={styles.card}>
              <div className={styles.cardHead}>
                <div>
                  <p className={styles.cardTitle}>{item.name}</p>
                  {item.description ? (
                    <p className={styles.cardMeta}>{item.description}</p>
                  ) : null}
                </div>
                <StatusPill tone={item.status === 'active' ? 'success' : 'neutral'}>
                  {t(item.status === 'active' ? 'hrWorkflows.statusActive' : 'hrWorkflows.statusDraft')}
                </StatusPill>
              </div>

              <div className={styles.chain}>
                {item.chain.map((step, index) => (
                  <span key={`${item.id}-${step}`} className={styles.chainStep}>
                    <span className={styles.chainChip}>{step}</span>
                    {index < item.chain.length - 1 ? <ChevronRight size={14} /> : null}
                  </span>
                ))}
              </div>

              <Button
                size="sm"
                variant="secondary"
                disabled={toggle.isPending}
                onClick={() => toggle.mutate(item.id)}
              >
                {t(item.status === 'active' ? 'hrWorkflows.deactivate' : 'hrWorkflows.activate')}
              </Button>
            </Card>
          ))}
        </div>
      )}

      <Modal
        open={formOpen}
        onClose={() => setFormOpen(false)}
        title={t('hrWorkflows.formTitle')}
        footer={
          <div className={styles.modalActions}>
            <Button variant="secondary" onClick={() => setFormOpen(false)}>
              {t('hrWorkflows.cancel')}
            </Button>
            <Button
              disabled={form.name.trim().length < 2 || chainSteps.length === 0 || create.isPending}
              onClick={() => create.mutate()}
            >
              {t('hrWorkflows.submit')}
            </Button>
          </div>
        }
      >
        <FormField label={t('hrWorkflows.name')} htmlFor={ids.name}>
          <Input
            placeholder={t('hrWorkflows.namePlaceholder')}
            value={form.name}
            onChange={(event) => setForm({ ...form, name: event.target.value })}
          />
        </FormField>
        <FormField label={t('hrWorkflows.description')} htmlFor={ids.description}>
          <Input
            placeholder={t('hrWorkflows.descriptionPlaceholder')}
            value={form.description}
            onChange={(event) => setForm({ ...form, description: event.target.value })}
          />
        </FormField>
        <FormField
          label={t('hrWorkflows.chain')}
          htmlFor={ids.chain}
          hint={t('hrWorkflows.chainHint')}
        >
          <Input
            placeholder={t('hrWorkflows.chainPlaceholder')}
            value={form.chain}
            onChange={(event) => setForm({ ...form, chain: event.target.value })}
          />
        </FormField>
      </Modal>
    </>
  )
}

// ---------------------------------------------------------------------------
// Rozwój (spec 01 §16)
// ---------------------------------------------------------------------------

/** Ton pigułki pozycji rozwoju — wniosek czeka, zatwierdzone i ukończone są faktem. */
const DEV_STATUS_TONE: Record<DevItemStatus, 'neutral' | 'success' | 'pink'> = {
  wniosek: 'neutral',
  zatwierdzone: 'success',
  w_trakcie: 'pink',
  ukonczone: 'success',
}

export function Development() {
  const { t } = useTranslation()
  const toast = useToast()
  const queryClient = useQueryClient()
  const ids = { employee: useId(), title: useId(), form: useId(), cost: useId(), budget: useId() }
  /*
   * Ekran widzi każdy z „Kadry : Podgląd" (także payroll), ale zapis pozycji
   * wymaga „Kadry : Edycja", a budżet „Konfiguracja : Zarządzanie". Akcje,
   * które skończyłyby się odmową serwera, nie są renderowane [D28].
   */
  const canEditItems = useHasPermission('Kadry : Edycja')
  const canSetBudget = useHasPermission('Konfiguracja : Zarządzanie')

  const [itemOpen, setItemOpen] = useState(false)
  const [budgetOpen, setBudgetOpen] = useState(false)
  const [form, setForm] = useState({ employeeId: '', title: '', form: '', cost: '' })
  const [budget, setBudget] = useState('')
  const [error, setError] = useState<string | null>(null)
  /* Błąd okna budżetu ma własne zdanie i własne miejsce (grupa 145). */
  const [budgetError, setBudgetError] = useState<string | null>(null)

  const development = useQuery({ queryKey: ['development'], queryFn: () => api.development() })
  const employees = useQuery({
    queryKey: ['employees', ''],
    queryFn: () => api.employees(),
    enabled: itemOpen,
  })

  const createItem = useMutation({
    mutationFn: () =>
      api.createDevelopmentItem({
        employeeId: form.employeeId,
        title: form.title.trim(),
        ...(form.form.trim() ? { form: form.form.trim() } : {}),
        ...(form.cost.trim() ? { costGr: Math.round(Number(form.cost.replace(',', '.')) * 100) } : {}),
      }),
    onSuccess: () => {
      toast.show(t('hrDevelopment.created'), { tone: 'success' })
      setItemOpen(false)
      setForm({ employeeId: '', title: '', form: '', cost: '' })
      void queryClient.invalidateQueries({ queryKey: ['development'] })
    },
    onError: () => setError('hrDevelopment.errorGeneric'),
  })

  const saveBudget = useMutation({
    mutationFn: () =>
      api.setDevelopmentBudget(
        development.data?.year ?? apiYear(),
        Math.round(Number(budget.replace(',', '.')) * 100),
      ),
    onSuccess: () => {
      toast.show(t('hrDevelopment.budgetSaved'), { tone: 'success' })
      setBudgetOpen(false)
      setBudgetError(null)
      void queryClient.invalidateQueries({ queryKey: ['development'] })
    },
    onError: () => setBudgetError('hrDevelopment.budgetError'),
  })

  /*
   * Zatwierdzenie i dalsze kroki pozycji (fala 8, grupa 58; D105). Ponad
   * budżet serwer nie blokuje — mówi o przekroczeniu, a ekran to powtarza.
   */
  const changeStatus = useMutation({
    mutationFn: (input: { id: string; status: DevItemStatus }) =>
      api.setDevelopmentItemStatus(input.id, input.status),
    onSuccess: (result) => {
      toast.show(t(`hrDevelopment.statusChanged.${result.status}`), { tone: 'success' })
      if (result.budget?.exceeded) {
        toast.show(
          t('hrDevelopment.budgetExceeded', {
            used: formatPLN(result.budget.usedGr),
            limit: formatPLN(result.budget.limitGr),
          }),
          { tone: 'info' },
        )
      }
      setError(null)
      void queryClient.invalidateQueries({ queryKey: ['development'] })
    },
    onError: (caught) =>
      setError(
        caught instanceof ApiError && caught.code === 'INVALID_TRANSITION'
          ? 'hrDevelopment.errorTransition'
          : 'hrDevelopment.errorGeneric',
      ),
  })

  if (development.isPending) return <Spinner label={t('common.loading')} />

  const data = development.data
  const budgetPercent =
    data?.budget && data.budget.limitGr > 0
      ? Math.min(100, Math.round((data.budget.usedGr / data.budget.limitGr) * 100))
      : 0

  return (
    <>
      <div className={styles.header}>
        <p className={styles.lead}>{t('hrDevelopment.lead')}</p>
        {canEditItems ? (
          <Button
            onClick={() => {
              setError(null)
              setItemOpen(true)
            }}
          >
            {t('hrDevelopment.add')}
          </Button>
        ) : null}
      </div>

      {error !== null ? (
        <InfoBanner tone="danger" className={styles.banner}>
          {t(error)}
        </InfoBanner>
      ) : null}

      <Card pad="md" className={styles.budget}>
        <div className={styles.budgetHead}>
          <span className={styles.budgetLabel}>
            {t('hrDevelopment.budgetTitle', { year: data?.year ?? '' })}
          </span>
          {canSetBudget ? (
            <Button
              size="sm"
              variant="secondary"
              onClick={() => {
                setBudget(data?.budget ? String(data.budget.limitGr / 100) : '')
                setBudgetError(null)
                setBudgetOpen(true)
              }}
            >
              {t('hrDevelopment.budgetSet')}
            </Button>
          ) : null}
        </div>
        {data?.budget ? (
          <>
            <p className={styles.budgetValue}>
              {t('hrDevelopment.budgetUsed', {
                used: formatPLN(data.budget.usedGr),
                limit: formatPLN(data.budget.limitGr),
              })}
            </p>
            <div className={styles.budgetTrack}>
              <span className={styles.budgetFill} style={{ width: `${budgetPercent}%` }} />
            </div>
            <p className={styles.budgetLeft}>
              {t('hrDevelopment.budgetLeft', {
                amount: formatPLN(Math.max(0, data.budget.limitGr - data.budget.usedGr)),
              })}
            </p>
          </>
        ) : (
          <p className={styles.budgetNone}>{t('hrDevelopment.budgetNone')}</p>
        )}
      </Card>

      {(data?.items ?? []).length === 0 ? (
        <EmptyState
          icon={<GraduationCap size={28} />}
          title={t('hrDevelopment.empty')}
          description={t('hrDevelopment.emptyBody')}
        />
      ) : (
        <Card pad="flush" className={styles.list}>
          {(data?.items ?? []).map((item) => (
            <div key={item.id} className={styles.row}>
              <div className={styles.rowBody}>
                <p className={styles.rowTitle}>{item.title}</p>
                <p className={styles.rowMeta}>
                  {item.firstName} {item.lastName}
                  {item.form ? ` · ${item.form}` : ''}
                </p>
              </div>
              {item.costGr !== null ? (
                <span className={styles.rowCost}>{formatPLN(item.costGr)}</span>
              ) : null}
              <StatusPill tone={DEV_STATUS_TONE[item.status]}>
                {t(`hrDevelopment.status.${item.status}`)}
              </StatusPill>
              {canEditItems
                ? DEV_ITEM_TRANSITIONS[item.status].map((next) => (
                    <Button
                      key={next}
                      size="sm"
                      variant={next === 'zatwierdzone' ? 'success' : 'secondary'}
                      disabled={changeStatus.isPending}
                      onClick={() => changeStatus.mutate({ id: item.id, status: next })}
                    >
                      {t(`hrDevelopment.actions.${next}`)}
                    </Button>
                  ))
                : null}
            </div>
          ))}
        </Card>
      )}

      <Modal
        open={itemOpen}
        onClose={() => setItemOpen(false)}
        title={t('hrDevelopment.formTitle')}
        footer={
          <div className={styles.modalActions}>
            <Button variant="secondary" onClick={() => setItemOpen(false)}>
              {t('hrDevelopment.cancel')}
            </Button>
            <Button
              disabled={form.employeeId === '' || form.title.trim().length < 2 || createItem.isPending}
              onClick={() => createItem.mutate()}
            >
              {t('hrDevelopment.submit')}
            </Button>
          </div>
        }
      >
        <FormField label={t('hrDevelopment.employee')} htmlFor={ids.employee}>
          <Select
            value={form.employeeId}
            onChange={(event) => setForm({ ...form, employeeId: event.target.value })}
          >
            <option value="">—</option>
            {/* Rozwój planuje się dla zatrudnionych — bez byłych pracowników i kandydatów (fala 9b). */}
            {(employees.data?.items ?? [])
              .filter((employee) => employee.status === 'aktywny')
              .map((employee) => (
                <option key={employee.id} value={employee.id}>
                  {employee.firstName} {employee.lastName}
                </option>
              ))}
          </Select>
        </FormField>
        <FormField label={t('hrDevelopment.title')} htmlFor={ids.title}>
          <Input
            placeholder={t('hrDevelopment.titlePlaceholder')}
            value={form.title}
            onChange={(event) => setForm({ ...form, title: event.target.value })}
          />
        </FormField>
        <FormField label={t('hrDevelopment.form')} htmlFor={ids.form}>
          <Input
            placeholder={t('hrDevelopment.formPlaceholder')}
            value={form.form}
            onChange={(event) => setForm({ ...form, form: event.target.value })}
          />
        </FormField>
        <FormField label={t('hrDevelopment.cost')} htmlFor={ids.cost}>
          <Input
            inputMode="decimal"
            value={form.cost}
            onChange={(event) => setForm({ ...form, cost: event.target.value })}
          />
        </FormField>
      </Modal>

      <Modal
        open={budgetOpen}
        onClose={() => setBudgetOpen(false)}
        title={t('hrDevelopment.budgetSet')}
        footer={
          <div className={styles.modalActions}>
            <Button variant="secondary" onClick={() => setBudgetOpen(false)}>
              {t('hrDevelopment.cancel')}
            </Button>
            <Button disabled={saveBudget.isPending} onClick={() => saveBudget.mutate()}>
              {t('hrDevelopment.budgetSubmit')}
            </Button>
          </div>
        }
      >
        {budgetError !== null ? (
          <InfoBanner tone="danger" className={styles.banner}>
            {t(budgetError)}
          </InfoBanner>
        ) : null}
        <FormField label={t('hrDevelopment.budgetLabel')} htmlFor={ids.budget}>
          <Input
            inputMode="decimal"
            value={budget}
            onChange={(event) => setBudget(event.target.value)}
          />
        </FormField>
      </Modal>
    </>
  )
}

// ---------------------------------------------------------------------------
// Raporty (spec 01 §11)
// ---------------------------------------------------------------------------

export function Reports() {
  const fmt = useFormat()
  const { t } = useTranslation()
  const reports = useQuery({ queryKey: ['reports'], queryFn: () => api.reportsSummary() })

  if (reports.isPending) return <Spinner label={t('common.loading')} />
  if (reports.isError) {
    return <EmptyState title={t('common.error')} description={t('common.retry')} />
  }

  const data = reports.data
  const empty = data.headcount.active === 0 && data.payrollCostGr === 0

  if (empty) {
    return (
      <>
        <p className={styles.lead}>{t('hrReports.lead')}</p>
        <EmptyState
          icon={<ChartBar size={28} />}
          title={t('hrReports.empty')}
          description={t('hrReports.emptyBody')}
        />
      </>
    )
  }

  const maxCost = Math.max(...data.byDepartment.map((row) => row.costGr), 1)
  const maxTrend = Math.max(...data.monthlyTrend.map((row) => row.costGr), 1)

  return (
    <>
      <p className={styles.lead}>{t('hrReports.lead')}</p>

      <Card pad="lg" className={styles.reportHead}>
        <span className={styles.reportLabel}>{t('hrReports.costTitle')}</span>
        <p className={styles.reportValue}>{formatPLN(data.payrollCostGr)}</p>
        <p className={styles.reportNote}>{t('hrReports.costNote')}</p>
        {/*
          Szereg miesięczny pojawia się po zamknięciu pierwszej listy płac
          [D29] — do tego czasu nota mówi, czego brakuje, a nie rysujemy
          wykresu z zer.
        */}
        {data.monthlyTrend.length === 0 ? (
          <p className={styles.reportNote}>{t('hrReports.trendEmpty')}</p>
        ) : null}
      </Card>

      <div className={styles.kpiGrid}>
        <StatCard
          label={t('hrReports.headcount')}
          value={data.headcount.active}
          note={`${t('hrReports.headcountOnboarding', { count: data.headcount.onboarding })} · ${t('hrReports.headcountTerminated', { count: data.headcount.terminated })}`}
        />
        <StatCard
          label={t('hrReports.leaves')}
          value={data.leaveDays.approved}
          note={t('hrReports.leavesPending', { count: data.leaveDays.pending })}
        />
        <StatCard
          label={t('hrReports.overtime')}
          value={data.overtimeHours}
          note={t('hrReports.overtimeHours', { month: fmt.month(data.overtimeMonth, 'period') })}
        />
      </div>

      {data.monthlyTrend.length > 0 ? (
        <section>
          <h3 className={styles.sectionTitle}>{t('hrReports.trendTitle')}</h3>
          <Card pad="md" className={styles.bars}>
            {data.monthlyTrend.map((row) => (
              <div key={row.month} className={styles.bar}>
                <span className={styles.barLabel}>{fmt.month(row.month, 'short')}</span>
                <span className={styles.barTrack}>
                  <span
                    className={styles.barFill}
                    style={{ width: `${Math.round((row.costGr / maxTrend) * 100)}%` }}
                  />
                </span>
                <span className={styles.barValue}>{formatPLN(row.costGr)}</span>
              </div>
            ))}
          </Card>
          <p className={styles.reportNote}>{t('hrReports.trendNote')}</p>
        </section>
      ) : null}

      <section>
        <h3 className={styles.sectionTitle}>{t('hrReports.byDepartment')}</h3>
        <Card pad="md" className={styles.bars}>
          {data.byDepartment.map((row) => (
            <div key={row.department || 'none'} className={styles.bar}>
              <span className={styles.barLabel}>
                {row.department || t('hrReports.noDepartment')}
              </span>
              <span className={styles.barTrack}>
                <span
                  className={styles.barFill}
                  style={{ width: `${Math.round((row.costGr / maxCost) * 100)}%` }}
                />
              </span>
              <span className={styles.barValue}>{formatPLN(row.costGr)}</span>
            </div>
          ))}
        </Card>
      </section>
    </>
  )
}

// ---------------------------------------------------------------------------
// Konfiguracja (spec 01 §12a)
// ---------------------------------------------------------------------------

const TILE_TARGET: Record<string, string> = {
  structure: '/app/settings/structure',
  roles: '/app/settings/roles',
  payrollParameters: '/app/settings/payroll',
  workflows: '/app/workflows',
  integrations: '/app/integrations',
  payrollRules: '/app/settings/rules/new',
  dictionaries: '/app/settings/dictionaries',
}

export function SettingsTiles() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  /* Rejestr czynności to dokument administratora — nie każdy go pobiera [D28]. */
  const canManageConfig = useHasPermission('Konfiguracja : Zarządzanie')
  const tiles = useQuery({ queryKey: ['settings', 'tiles'], queryFn: () => api.settingsTiles() })

  if (tiles.isPending) return <Spinner label={t('common.loading')} />
  if (tiles.isError) {
    return <EmptyState title={t('common.error')} description={t('common.retry')} />
  }

  return (
    <>
      <p className={styles.lead}>{t('hrSettings.lead')}</p>

      <div className={styles.tiles}>
        {tiles.data.tiles.map((tile) => {
          const target = TILE_TARGET[tile.key]
          return (
            <Card key={tile.key} pad="md" className={styles.tile}>
              <div className={styles.tileHead}>
                <span className={styles.tileIcon} aria-hidden="true">
                  <Settings size={18} />
                </span>
                <StatusPill
                  tone={
                    tile.status === 'configured'
                      ? 'success'
                      : tile.status === 'attention'
                        ? 'warning'
                        : 'neutral'
                  }
                >
                  {t(`hrSettings.status.${tile.status}`)}
                </StatusPill>
              </div>
              <p className={styles.tileTitle}>{t(`hrSettings.tiles.${tile.key}`)}</p>
              <p className={styles.tileDesc}>{t(`hrSettings.tiles.${tile.key}Desc`)}</p>
              {tile.status === 'soon' ? (
                <p className={cx(styles.tileDesc, styles.tileSoon)}>{t('hrSettings.soonNote')}</p>
              ) : null}
              {target ? (
                <Button size="sm" variant="secondary" onClick={() => navigate(target)}>
                  {t('hrSettings.open')}
                </Button>
              ) : null}
            </Card>
          )
        })}
      </div>

      {/*
        Rejestr czynności przetwarzania (art. 30 RODO, Etap 26). Powstaje z tego,
        co system faktycznie robi, ale administrator musi go POTWIERDZIĆ
        i uzupełnić o dane, których system nie zna — mówimy to wprost.
      */}
      {canManageConfig ? (
        <Card pad="md" className={styles.registerCard}>
          <p className={styles.tileTitle}>{t('hrSettings.register.title')}</p>
          <p className={styles.tileDesc}>{t('hrSettings.register.lead')}</p>
          <p className={styles.tileDesc}>{t('hrSettings.register.confirm')}</p>
          <Button
            size="sm"
            variant="secondary"
            onClick={() => {
              openFile(api.processingRegisterUrl())
            }}
          >
            {t('hrSettings.register.download')}
          </Button>
        </Card>
      ) : null}

      <p className={styles.footnote}>
        <Badge tone="soft">{t('hrSettings.status.soon')}</Badge> {t('hrSettings.soonNote')}
      </p>
    </>
  )
}
